import { type Event, createEvent as createEventModel } from 'models';
import { queryClient, useMutation } from 'query';
import { handleAxiosError } from 'ui';
import type { ApiClientResponseError, ResponseErrorDataObject } from 'common/services/api';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { getCalendarPrincipalEventsListQueryKey } from 'features/calendar/queries';
import { createEvent } from '../../services';

export type DuplicateEventData =
  | DuplicateReminderEventData
  | DuplicateVideoConferenceEventData
  | DuplicateWebinarEventData;

export type DuplicateEventRequestData = {
  data: DuplicateEventData;
  duplicateParticipantsFrom?: string;
};

type CreateChannelEventData = {
  featuredInChannels?: boolean;
  organization?: string;
  showInChannel?: boolean;
  tags?: string[];
};

type DistributionList = {
  communityId: string;
  communityName: string;
};

type DuplicateBaseEventData = {
  distributionList?: DistributionList[];
  endTime: string;
  html?: string;
  name: string;
  startTime: string;
  text?: string;
};

type DuplicateReminderEventData = {
  allDay: boolean;
  type: 'reminder';
} & DuplicateBaseEventData;

type DuplicateVideoConferenceEventData = {
  channel: CreateChannelEventData;
  type: 'video_conference';
} & DuplicateBaseEventData;

type DuplicateWebinarEventData = {
  channel: CreateChannelEventData;
  type: 'webinar';
} & DuplicateBaseEventData;

const defaultMutateOnError = (error: ApiClientResponseError<ResponseErrorDataObject>) => {
  handleAxiosError(error, 'Failed to duplicate event.');
};

export const useDuplicateEvent = () =>
  useMutation<Event, DuplicateEventRequestData>({
    defaultMutateOnError,
    async mutationFn({ data, duplicateParticipantsFrom }) {
      const response = await createEvent({
        body: { ...data, participants: [] },
        config: {
          params: { duplicateParticipantsFrom },
        },
      });

      return createEventModel(response.data);
    },
    async onSettled() {
      await queryClient.invalidateQueries({ queryKey: getCalendarPrincipalEventsListQueryKey({}) });
    },
  });
